import { combineReducers } from 'redux'
import { omit, omitBy } from 'lodash'

import {
  UPDATE_REPORT_LAYER,
  ADD_REPORT_LAYER_TO_MAP,
  UPDATE_REPORT_LAYER_MARKER_STYLE,
  TOGGLE_LAYER,
  RESET_MAP,
  ADD_PROXIMITY_LAYER_TO_MAP,
  UPDATE_PROXIMITY_LAYER,
  REMOVE_PROXIMITY_LAYER,
  ADD_ISOCHRONE_LAYER_TO_MAP,
  UPDATE_ISOCHRONE_LAYER,
  REMOVE_ISOCHRONE_LAYER,
  REMOVE_LAYER,
  REMOVE_PROXIMITY_BY_REPORT,
  REMOVE_ISOCHRONE_BY_REPORT,
  REMOVE_ENRICHMENT_BY_REPORT,
  ADD_TEGOLA_LAYER_TO_MAP,
  UPDATE_DEMOGRAPHIC_LAYER,
  EDIT_REPORT_LAYER_DATA,
  ADD_POI_LAYER_TO_MAP,
  UPDATE_POI_LAYER_MARKER_STYLE,
  ADD_ENRICHMENT,
  UPDATE_ENRICHMENT_DATA,
  TOGGLE_ENRICHMENT,
  UPDATE_ENRICHMENT_FILL_STYLE,
  SET_ENRICHMENT_METRIC,
  SET_ENRICHMENT_FILTER_RANGE,
  CLEAR_ENRICHMENT_DATA,
} from '../../../constants/types'


const demographic = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_TEGOLA_LAYER_TO_MAP:
    return {
      ...state,
      [payload.layerProfile.layer_id]: payload,
    }
  case UPDATE_DEMOGRAPHIC_LAYER:
    return {
      ...state,
      [payload.layerID]: {
        ...state[payload.layerID],
        ...payload.update,
      },
    }
  case TOGGLE_LAYER:
    if (payload in state) {
      return {
        ...state,
        [payload]: {
          ...state[payload],
          enabled: !state[payload].enabled,
        },
      }
    }
    return state
  case REMOVE_LAYER:
    return omit(state, payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const report = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_REPORT_LAYER_TO_MAP:
    return {
      ...state,
      [payload.layer_id]: payload.layer,
    }
  case UPDATE_REPORT_LAYER_MARKER_STYLE:
    return {
      ...state,
      [payload.layerID]: {
        ...state[payload.layerID],
        markerStyle: {
          ...state[payload.layerID].markerStyle,
          ...payload.update,
        },
      },
    }
  case TOGGLE_LAYER:
    if (payload in state) {
      return {
        ...state,
        [payload]: {
          ...state[payload],
          enabled: !state[payload].enabled,
        },
      }
    }
    return state
  case UPDATE_REPORT_LAYER:
    return {
      ...state,
      [payload.layerID]: {
        ...state[payload.layerID],
        ...payload.update,
      },
    }
  case EDIT_REPORT_LAYER_DATA: {
    const data = { ...state[payload.layerID].data[payload.id], ...payload.data }

    return {
      ...state,
      [payload.layerID]: {
        ...state[payload.layerID],
        data: {
          ...state[payload.layerID].data,
          [payload.id]: data,
        },
      },
    }
  }
  case REMOVE_LAYER:
    return omit(state, payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const enrichment = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_ENRICHMENT:
    return {
      ...state,
      [payload.id]: payload,
    }
  case UPDATE_ENRICHMENT_FILL_STYLE:
    return {
      ...state,
      [payload.id]: {
        ...state[payload.poi_id],
        style: payload.update,
      },
    }
  case UPDATE_ENRICHMENT_DATA:
    return {
      ...state,
      [payload.id]: {
        ...state[payload.id],
        geojson: payload.replace ? [...payload.update] : [
          ...state[payload.id].geojson,
          ...payload.update,
        ],
        level: payload.level,
      },
    }
  case TOGGLE_ENRICHMENT:
    if (payload.id in state) {
      let enabled = !state[payload.id].enabled
      if (payload.status) {
        if (payload.status === 'hide') {
          enabled = false
        } else {
          enabled = true
        }
      }
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          enabled,
        },
      }
    }
    return state
  case SET_ENRICHMENT_METRIC:
    if (payload.id in state) {
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          metric: payload.metric,
          style: payload.newStyle,
          data: state[payload.id].data.map((d) => {
            const updatedFeature = { ...d }
            updatedFeature.properties.metric = payload.metric
            return updatedFeature
          }),
          filtered: false,
        },
      }
    }
    return state
  case SET_ENRICHMENT_FILTER_RANGE:
    if (payload.id in state) {
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          filtered: true,
          min: payload.min,
          max: payload.max,
        },
      }
    }
    return state
  case REMOVE_ENRICHMENT_BY_REPORT:
    return omitBy(state, (layer) => layer.layer_id === payload)
  case CLEAR_ENRICHMENT_DATA:
    return {}
  default:
    return state
  }
}

const proximity = (state = {}, { type, payload = {} }) => {
  switch (type) {
  case ADD_PROXIMITY_LAYER_TO_MAP:
    return {
      ...state,
      [payload.id]: payload,
    }
  case UPDATE_PROXIMITY_LAYER:
    if (state[payload.id]) {
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          [payload.key]: payload.value,
        },
      }
    }
    return state
  case REMOVE_PROXIMITY_LAYER:
    return omit(state, payload)
  case REMOVE_PROXIMITY_BY_REPORT:
    return omitBy(state, (layer) => layer.layerID === payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const isochrone = (state = {}, { type, payload = {} }) => {
  switch (type) {
  case ADD_ISOCHRONE_LAYER_TO_MAP:
    return {
      ...state,
      [payload.id]: payload,
    }
  case UPDATE_ISOCHRONE_LAYER:
    if (state[payload.id]) {
      return {
        ...state,
        [payload.id]: {
          ...state[payload.id],
          [payload.key]: payload.value,
        },
      }
    }
    return state
  case REMOVE_ISOCHRONE_LAYER:
    return omit(state, payload)
  case REMOVE_ISOCHRONE_BY_REPORT:
    return omitBy(state, (layer) => layer.layerID === payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

const poi = (state = {}, { type, payload }) => {
  switch (type) {
  case ADD_POI_LAYER_TO_MAP:
    return {
      ...state,
      [payload.layer_id]: payload.layer,
    }
  case UPDATE_POI_LAYER_MARKER_STYLE:
    return {
      ...state,
      [payload.layerID]: {
        ...state[payload.layerID],
        markerStyle: {
          ...state[payload.layerID].markerStyle,
          ...payload.update,
        },
      },
    }
  case TOGGLE_LAYER:
    if (payload in state) {
      return {
        ...state,
        [payload]: {
          ...state[payload],
          enabled: !state[payload].enabled,
        },
      }
    }
    return state
  case REMOVE_LAYER:
    return omit(state, payload)
  case RESET_MAP:
    return {}
  default:
    return state
  }
}

export default combineReducers({
  demographic,
  report,
  enrichment,
  proximity,
  isochrone,
  poi,
})
